import { useCallback, useMemo, useState } from "react";
import type {
  ArtworkLayer,
  DesignSide,
  EditorState,
  Fill,
  Gender,
  WearableId,
} from "../types";
import { getWearable, getWearableFor } from "../config/wearables";
import { cloneFill, solidFill } from "../utils/fill";

/** Default starting paint for the shirt — matches the UI's dark panel tone. */
const DEFAULT_BASE_FILL: Fill = solidFill("#17191c");

export function useEditorState(initialGender: Gender = "male") {
  const initialWearable = getWearableFor(initialGender);

  const [state, setState] = useState<EditorState>({
    wearableId: initialWearable.id,
    selectedSide: "front",
    baseFill: cloneFill(DEFAULT_BASE_FILL),
    layers: [],
    selectedLayerId: null,
  });

  const config = useMemo(() => getWearable(state.wearableId), [state.wearableId]);

  const gender: Gender = config.gender;

  const setGender = useCallback((g: Gender) => {
    const w = getWearableFor(g);
    setState((s) => ({ ...s, wearableId: w.id }));
  }, []);

  const setWearableId = useCallback((id: WearableId) => {
    setState((s) => ({ ...s, wearableId: id }));
  }, []);

  const setSelectedSide = useCallback((side: DesignSide) => {
    setState((s) => ({ ...s, selectedSide: side }));
  }, []);

  const setBaseFill = useCallback((fill: Fill) => {
    setState((s) => ({ ...s, baseFill: cloneFill(fill) }));
  }, []);

  const addLayer = useCallback((layer: ArtworkLayer) => {
    setState((s) => ({
      ...s,
      layers: [...s.layers, layer],
      selectedLayerId: layer.id,
    }));
  }, []);

  const updateLayer = useCallback(
    (id: string, patch: Partial<ArtworkLayer>) => {
      setState((s) => ({
        ...s,
        layers: s.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)),
      }));
    },
    [],
  );

  const removeLayer = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      layers: s.layers.filter((l) => l.id !== id),
      selectedLayerId: s.selectedLayerId === id ? null : s.selectedLayerId,
    }));
  }, []);

  const selectLayer = useCallback((id: string | null) => {
    setState((s) => ({ ...s, selectedLayerId: id }));
  }, []);

  const reorderLayer = useCallback((id: string, direction: "up" | "down") => {
    setState((s) => {
      const idx = s.layers.findIndex((l) => l.id === id);
      if (idx < 0) return s;
      const target = direction === "up" ? idx + 1 : idx - 1;
      if (target < 0 || target >= s.layers.length) return s;
      const next = s.layers.slice();
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return { ...s, layers: next };
    });
  }, []);

  const resetDesign = useCallback(() => {
    setState((s) => ({
      ...s,
      baseFill: cloneFill(DEFAULT_BASE_FILL),
      layers: [],
      selectedLayerId: null,
    }));
  }, []);

  const selectedLayer = useMemo(
    () => state.layers.find((l) => l.id === state.selectedLayerId) ?? null,
    [state.layers, state.selectedLayerId],
  );

  return {
    state,
    config,
    gender,
    selectedLayer,
    setGender,
    setWearableId,
    setSelectedSide,
    setBaseFill,
    addLayer,
    updateLayer,
    removeLayer,
    selectLayer,
    reorderLayer,
    resetDesign,
  };
}
